import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { CreateDeviationsFromKaiRequest, CreateDeviationsFromKaiResponse } from './checklist.service';

// ══════════════════════════════════════════════════════════════
//  Response wrapper (shared with checklist.service)
// ══════════════════════════════════════════════════════════════

export interface ApiResponse<T> {
  success: boolean;
  result: T;
  errorMessage?: any[];
}

// ══════════════════════════════════════════════════════════════
//  Settings & Risk
// ══════════════════════════════════════════════════════════════

export interface HmsSettingsDto {
  riskMatrixSize: string;
  deviationCategories: string[];
  hmsResponsibleEmployeeNo: string;
  probabilityLabels: string[];
  consequenceLabels: string[];
}

export interface RiskCalculationResultDto {
  riskScore: number;
  severity: string;
  color: string;
  autoDueDays: number;
}

// ══════════════════════════════════════════════════════════════
//  Deviation interfaces
// ══════════════════════════════════════════════════════════════

export interface DeviationListDto {
  deviationId: number;
  title: string;
  category?: string;
  location?: string;
  riskScore: number;
  severity: string;
  status: string;
  dueDate?: string;
  executionId?: number;
  createdDate: string;
  assignedToName?: string;
  reportedByName?: string;
  templateName?: string;
}

export interface DeviationDetailDto {
  deviationId: number;
  executionId?: number;
  responseId?: number;
  title: string;
  description?: string;
  category?: string;
  location?: string;
  probability: number;
  consequence: number;
  riskScore: number;
  severity: string;
  assignedToUserId?: number;
  dueDate?: string;
  status: string;
  resolvedDate?: string;
  resolutionNotes?: string;
  reportedByUserId: number;
  createdDate: string;
  modifiedDate?: string;
  closingComment?: string;
  closedDate?: string;
  closedByName?: string;
  assignedToName?: string;
  reportedByName?: string;
  templateName?: string;
  checklistItemLabel?: string;
  actions: DeviationActionDto[];
  auditTrail: DeviationAuditDto[];
  comments: DeviationCommentDto[];
}

export interface CreateDeviationRequest {
  executionId?: number;
  responseId?: number;
  title: string;
  description?: string;
  category?: string;
  location?: string;
  probability: number;
  consequence: number;
  assignedToUserId?: number;
  dueDate?: string;
}

export interface UpdateDeviationRequest {
  deviationId: number;
  title: string;
  description?: string;
  category?: string;
  location?: string;
  probability: number;
  consequence: number;
  assignedToUserId?: number;
  dueDate?: string;
  changeReason?: string;
}

export interface UpdateDeviationStatusRequest {
  status: string;
  resolutionNotes?: string;
  closingComment?: string;
}

// ══════════════════════════════════════════════════════════════
//  Action interfaces
// ══════════════════════════════════════════════════════════════

export interface DeviationActionDto {
  actionId: number;
  deviationId: number;
  actionTitle: string;
  actionDescription?: string;
  assignedToUserId?: number;
  dueDate?: string;
  actionStatus: string;
  completedDate?: string;
  completionNotes?: string;
  createdDate: string;
  assignedToName?: string;
  completedByName?: string;
  createdByName?: string;
}

export interface CreateActionRequest {
  actionTitle: string;
  actionDescription?: string;
  assignedToUserId?: number;
  dueDate?: string;
}

export interface UpdateActionRequest {
  actionId: number;
  actionTitle: string;
  actionDescription?: string;
  assignedToUserId?: number;
  dueDate?: string;
}

export interface UpdateActionStatusRequest {
  actionStatus: string;
  completionNotes?: string;
}

// ══════════════════════════════════════════════════════════════
//  Audit
// ══════════════════════════════════════════════════════════════

export interface DeviationAuditDto {
  auditId: number;
  fieldName: string;
  oldValue?: string;
  newValue?: string;
  changedDate: string;
  changeReason?: string;
  changedByName?: string;
}

// ══════════════════════════════════════════════════════════════
//  Deviation Comments
// ══════════════════════════════════════════════════════════════

export interface DeviationCommentDto {
  commentId: number;
  deviationId: number;
  userId: number;
  userName?: string;
  commentText: string;
  createdDate: string;
  editedDate?: string;
}

export interface AddDeviationCommentRequest {
  deviationId: number;
  commentText: string;
}

export interface DeleteDeviationCommentRequest {
  commentId: number;
}

// ══════════════════════════════════════════════════════════════
//  Report
// ══════════════════════════════════════════════════════════════

export interface HmsReportDataDto {
  totalDeviations: number;
  openDeviations: number;
  resolvedDeviations: number;
  criticalDeviations: number;
  completedActions: number;
  overdueActions: number;
  riskMatrixSize: string;
  deviationsByCategory: CategoryCountDto[];
  safetyRounds: SafetyRoundSummaryDto[];
  recentDeviations: DeviationListDto[];
  riskAssessmentSummary: RiskAssessmentReportSummaryDto;
  goalSummary: GoalReportSummaryDto;
}

export interface RiskAssessmentReportSummaryDto {
  totalAssessments: number;
  completedAssessments: number;
  activeAssessments: number;
  totalRiskItems: number;
  highRiskItems: number;
  criticalRiskItems: number;
  mitigatedItems: number;
  riskItemsByCategory: CategoryCountDto[];
  riskItemsBySeverity: CategoryCountDto[];
}

export interface GoalReportSummaryDto {
  totalGoals: number;
  completedGoals: number;
  activeGoals: number;
  averageCompletionPercentage: number;
  totalGoalActions: number;
  completedGoalActions: number;
  overdueGoalActions: number;
  goalsByCategory: CategoryCountDto[];
}

export interface CategoryCountDto {
  category: string;
  count: number;
}

export interface SafetyRoundSummaryDto {
  executionId: number;
  templateName: string;
  completedDate?: string;
  deviationCount: number;
}

// ══════════════════════════════════════════════════════════════
//  Dashboard
// ══════════════════════════════════════════════════════════════

export interface HmsDashboardDto {
  openDeviations: number;
  criticalDeviations: number;
  overdueDeviations: number;
  unassignedDeviations: number;
  completedActions: number;
  totalActions: number;
  safetyRoundsLast12Months: number;
  deviationsByCategory: CategoryCountDto[];
  deviationTrend: MonthCountDto[];
  actionRequiredDeviations: DeviationListDto[];
}

export interface MonthCountDto {
  month: string;
  count: number;
}

// ══════════════════════════════════════════════════════════════
//  Handlingsplan (Action Plan)
// ══════════════════════════════════════════════════════════════

export interface HandlingsplanAvvikDto {
  deviationId: number;
  riskItemId?: number;
  source: string;
  title: string;
  category?: string;
  location?: string;
  severity: string;
  riskScore: number;
  status: string;
  dueDate?: string;
  erForfalt: boolean;
  assignedToName?: string;
  reportedByName?: string;
  createdDate: string;
  totalActions: number;
  completedActions: number;
  actions: HandlingsplanTiltakDto[];
}

export interface HandlingsplanTiltakDto {
  actionId: number;
  deviationId: number;
  riskItemId?: number;
  actionTitle: string;
  actionDescription?: string;
  assignedToName?: string;
  dueDate?: string;
  erForfalt: boolean;
  actionStatus: string;
  completedDate?: string;
}

export interface HandlingsplanResultDto {
  deviations: HandlingsplanAvvikDto[];
  totalOpen: number;
  totalOverdue: number;
  totalCritical: number;
  totalActions: number;
  completedActions: number;
  totalRiskItems: number;
}

export interface KaiAnalyseResultatDto {
  oppsummering: string;
  prioriteringer: PrioriteringspostDto[];
  tiltakForslag: string[];
  risikovurdering?: string;
}

export interface PrioriteringspostDto {
  deviationId: number;
  title: string;
  begrunnelse: string;
  prioritet: number;
}

// ══════════════════════════════════════════════════════════════
//  Risk Assessment interfaces
// ══════════════════════════════════════════════════════════════

export interface RiskAssessmentListDto {
  assessmentId: number;
  title: string;
  description?: string;
  periodFrom?: string;
  periodTo?: string;
  nextReviewDate?: string;
  status: string;
  responsibleName?: string;
  createdDate: string;
  itemCount: number;
  highestSeverity?: string;
  highRiskCount: number;
}

export interface RiskAssessmentDetailDto {
  assessmentId: number;
  title: string;
  description?: string;
  periodFrom?: string;
  periodTo?: string;
  nextReviewDate?: string;
  status: string;
  responsibleUserId?: number;
  responsibleName?: string;
  createdByUserId: number;
  createdByName?: string;
  createdDate: string;
  modifiedDate?: string;
  completedDate?: string;
  completedByName?: string;
  previousAssessmentId?: number;
  items: RiskAssessmentItemDto[];
}

export interface RiskAssessmentItemDto {
  itemId: number;
  assessmentId: number;
  itemOrder: number;
  category: string;
  description?: string;
  probability: number;
  consequence: number;
  riskScore: number;
  severity: string;
  existingMeasures?: string;
  plannedMeasures?: string;
  assignedToUserId?: number;
  assignedToName?: string;
  dueDate?: string;
  status: string;
  comment?: string;
  createdDate: string;
  modifiedDate?: string;
  linkedDeviationId?: number;
  actions: HmsRiskActionDto[];
}

export interface CreateRiskAssessmentRequest {
  title: string;
  description?: string;
  periodFrom?: string;
  periodTo?: string;
  nextReviewDate?: string;
  responsibleUserId?: number;
  populateDefaults: boolean;
}

export interface UpdateRiskAssessmentRequest {
  assessmentId: number;
  title: string;
  description?: string;
  periodFrom?: string;
  periodTo?: string;
  nextReviewDate?: string;
  responsibleUserId?: number;
}

export interface CreateRiskItemRequest {
  category: string;
  description?: string;
  probability: number;
  consequence: number;
  existingMeasures?: string;
  plannedMeasures?: string;
  assignedToUserId?: number;
  dueDate?: string;
  comment?: string;
}

export interface UpdateRiskItemRequest {
  itemId: number;
  category: string;
  description?: string;
  probability: number;
  consequence: number;
  existingMeasures?: string;
  plannedMeasures?: string;
  assignedToUserId?: number;
  dueDate?: string;
  status: string;
  comment?: string;
}

export interface ReorderItemsRequest {
  itemIds: number[];
}

// ══════════════════════════════════════════════════════════════
//  Goal interfaces
// ══════════════════════════════════════════════════════════════

export interface GoalListDto {
  goalId: number;
  title: string;
  category?: string;
  year: number;
  targetValue?: string;
  currentValue?: string;
  priority: number;
  status: string;
  responsibleName?: string;
  dueDate?: string;
  completionPercentage: number;
  createdDate: string;
  totalActions: number;
  completedActions: number;
}

export interface GoalDetailDto {
  goalId: number;
  title: string;
  description?: string;
  category?: string;
  year: number;
  targetValue?: string;
  currentValue?: string;
  priority: number;
  status: string;
  responsibleUserId?: number;
  responsibleName?: string;
  dueDate?: string;
  completionPercentage: number;
  completedDate?: string;
  completedByName?: string;
  createdByUserId: number;
  createdByName?: string;
  createdDate: string;
  modifiedDate?: string;
  actions: GoalActionDto[];
}

export interface GoalActionDto {
  actionId: number;
  goalId: number;
  actionTitle: string;
  actionDescription?: string;
  assignedToUserId?: number;
  assignedToName?: string;
  dueDate?: string;
  actionStatus: string;
  completedDate?: string;
  completedByName?: string;
  completionNotes?: string;
  createdByName?: string;
  createdDate: string;
}

export interface CreateGoalRequest {
  title: string;
  description?: string;
  category?: string;
  year: number;
  targetValue?: string;
  priority: number;
  responsibleUserId?: number;
  dueDate?: string;
}

export interface UpdateGoalRequest {
  goalId: number;
  title: string;
  description?: string;
  category?: string;
  year: number;
  targetValue?: string;
  currentValue?: string;
  priority: number;
  responsibleUserId?: number;
  dueDate?: string;
  completionPercentage: number;
}

export interface UpdateGoalStatusRequest {
  status: string;
}

export interface GoalSummaryDto {
  year: number;
  totalGoals: number;
  completedGoals: number;
  activeGoals: number;
  cancelledGoals: number;
  averageCompletionPercentage: number;
  totalActions: number;
  completedActionCount: number;
  goalsByCategory: CategoryCountDto[];
}

// ══════════════════════════════════════════════════════════════
//  Service
// ══════════════════════════════════════════════════════════════

@Injectable({
  providedIn: 'root'
})
export class HmsService {
  private get apiUrl(): string {
    return `${this.configService.get('apiBaseUrl')}/Hms`;
  }

  constructor(
    private http: HttpClient,
    private configService: ConfigService
  ) { }

  // ── Settings & Risk ───────────────────────────────────────

  getHmsSettings(): Observable<HmsSettingsDto> {
    return this.http.get<ApiResponse<HmsSettingsDto>>(
      `${this.apiUrl}/GetHmsSettings`
    ).pipe(map(r => r.result));
  }

  calculateRisk(probability: number, consequence: number): Observable<RiskCalculationResultDto> {
    return this.http.get<ApiResponse<RiskCalculationResultDto>>(
      `${this.apiUrl}/CalculateRisk?probability=${probability}&consequence=${consequence}`
    ).pipe(map(r => r.result));
  }

  getDeviationCategories(): Observable<string[]> {
    return this.http.get<ApiResponse<string[]>>(
      `${this.apiUrl}/GetDeviationCategories`
    ).pipe(map(r => r.result || []));
  }

  // ── Deviations ────────────────────────────────────────────

  getDeviations(status?: string, severity?: string, fromDate?: string, toDate?: string): Observable<DeviationListDto[]> {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    if (severity) params = params.set('severity', severity);
    if (fromDate) params = params.set('fromDate', fromDate);
    if (toDate) params = params.set('toDate', toDate);
    return this.http.get<ApiResponse<DeviationListDto[]>>(
      `${this.apiUrl}/GetDeviations`, { params }
    ).pipe(map(r => r.result || []));
  }

  getDeviationsByExecution(executionId: number): Observable<DeviationListDto[]> {
    return this.http.get<ApiResponse<DeviationListDto[]>>(
      `${this.apiUrl}/GetDeviationsByExecution/${executionId}`
    ).pipe(map(r => r.result || []));
  }

  getDeviation(deviationId: number): Observable<DeviationDetailDto | null> {
    return this.http.get<ApiResponse<DeviationDetailDto | null>>(
      `${this.apiUrl}/GetDeviation/${deviationId}`
    ).pipe(map(r => r.result));
  }

  createDeviation(request: CreateDeviationRequest): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(
      `${this.apiUrl}/CreateDeviation`, request
    );
  }

  updateDeviation(request: UpdateDeviationRequest): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(
      `${this.apiUrl}/UpdateDeviation`, request
    );
  }

  updateDeviationStatus(deviationId: number, request: UpdateDeviationStatusRequest): Observable<ApiResponse<boolean>> {
    return this.http.patch<ApiResponse<boolean>>(
      `${this.apiUrl}/UpdateDeviationStatus/${deviationId}`, request
    );
  }

  deleteDeviation(deviationId: number): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(
      `${this.apiUrl}/DeleteDeviation/${deviationId}`
    );
  }

  // ── Actions ───────────────────────────────────────────────

  createAction(deviationId: number, request: CreateActionRequest): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(
      `${this.apiUrl}/CreateAction/${deviationId}`, request
    );
  }

  updateAction(deviationId: number, request: UpdateActionRequest): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(
      `${this.apiUrl}/UpdateAction/${deviationId}`, request
    );
  }

  updateActionStatus(deviationId: number, actionId: number, request: UpdateActionStatusRequest): Observable<ApiResponse<boolean>> {
    return this.http.patch<ApiResponse<boolean>>(
      `${this.apiUrl}/UpdateActionStatus/${deviationId}/${actionId}`, request
    );
  }

  deleteAction(deviationId: number, actionId: number): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(
      `${this.apiUrl}/DeleteAction/${deviationId}/${actionId}`
    );
  }

  // ── Audit ─────────────────────────────────────────────────

  getAuditTrail(deviationId: number): Observable<DeviationAuditDto[]> {
    return this.http.get<ApiResponse<DeviationAuditDto[]>>(
      `${this.apiUrl}/GetAuditTrail/${deviationId}`
    ).pipe(map(r => r.result || []));
  }

  // ── Deviation Comments ────────────────────────────────────

  getDeviationComments(deviationId: number): Observable<DeviationCommentDto[]> {
    return this.http.get<ApiResponse<DeviationCommentDto[]>>(
      `${this.apiUrl}/GetDeviationComments/${deviationId}`
    ).pipe(map(r => r.result || []));
  }

  addDeviationComment(request: AddDeviationCommentRequest): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(
      `${this.apiUrl}/AddDeviationComment`, request
    );
  }

  deleteDeviationComment(commentId: number): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(
      `${this.apiUrl}/DeleteDeviationComment`, { commentId }
    );
  }

  // ── Reports ───────────────────────────────────────────────

  getDashboardData(): Observable<HmsDashboardDto> {
    return this.http.get<ApiResponse<HmsDashboardDto>>(
      `${this.apiUrl}/GetDashboardData`
    ).pipe(map(r => r.result));
  }

  getHmsReportData(fromDate: string, toDate: string): Observable<HmsReportDataDto> {
    return this.http.get<ApiResponse<HmsReportDataDto>>(
      `${this.apiUrl}/GetHmsReportData?fromDate=${fromDate}&toDate=${toDate}`
    ).pipe(map(r => r.result));
  }

  generateHmsAiReport(prompt: string): Observable<string> {
    return this.http.post<ApiResponse<string>>(
      `${this.apiUrl}/GenerateHmsAiReport`, { prompt }
    ).pipe(map(r => r.result));
  }

  getMinSafetyRoundYear(): Observable<number | null> {
    return this.http.get<ApiResponse<number | null>>(
      `${this.apiUrl}/GetMinSafetyRoundYear`
    ).pipe(map(r => r.result ?? null));
  }

  // ── Handlingsplan (Action Plan) ───────────────────────────

  getHandlingsplan(kategori?: string, alvorlighetsgrad?: string): Observable<HandlingsplanResultDto> {
    let params = new HttpParams();
    if (kategori) params = params.set('kategori', kategori);
    if (alvorlighetsgrad) params = params.set('alvorlighetsgrad', alvorlighetsgrad);
    return this.http.get<ApiResponse<HandlingsplanResultDto>>(
      `${this.apiUrl}/GetHandlingsplan`, { params }
    ).pipe(map(r => r.result));
  }

  analyzeHandlingsplan(deviations: HandlingsplanAvvikDto[]): Observable<ApiResponse<KaiAnalyseResultatDto>> {
    return this.http.post<ApiResponse<KaiAnalyseResultatDto>>(
      `${this.apiUrl}/AnalyzeHandlingsplan`, { deviations }
    );
  }

  // ── KAI Batch Deviation Creation ──────────────────────────

  createDeviationsFromKai(request: CreateDeviationsFromKaiRequest): Observable<ApiResponse<CreateDeviationsFromKaiResponse>> {
    return this.http.post<ApiResponse<CreateDeviationsFromKaiResponse>>(
      `${this.apiUrl}/CreateDeviationsFromKai`, request
    );
  }

  // ── Risk Assessments ──────────────────────────────────────

  getRiskAssessments(status?: string, year?: number): Observable<RiskAssessmentListDto[]> {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    if (year) params = params.set('year', year.toString());
    return this.http.get<ApiResponse<RiskAssessmentListDto[]>>(
      `${this.apiUrl}/GetRiskAssessments`, { params }
    ).pipe(map(r => r.result || []));
  }

  getRiskAssessment(assessmentId: number): Observable<RiskAssessmentDetailDto | null> {
    return this.http.get<ApiResponse<RiskAssessmentDetailDto | null>>(
      `${this.apiUrl}/GetRiskAssessment/${assessmentId}`
    ).pipe(map(r => r.result));
  }

  createRiskAssessment(request: CreateRiskAssessmentRequest): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(
      `${this.apiUrl}/CreateRiskAssessment`, request
    );
  }

  updateRiskAssessment(request: UpdateRiskAssessmentRequest): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(
      `${this.apiUrl}/UpdateRiskAssessment`, request
    );
  }

  deleteRiskAssessment(assessmentId: number): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(
      `${this.apiUrl}/DeleteRiskAssessment/${assessmentId}`
    );
  }

  completeRiskAssessment(assessmentId: number): Observable<ApiResponse<boolean>> {
    return this.http.patch<ApiResponse<boolean>>(
      `${this.apiUrl}/CompleteRiskAssessment/${assessmentId}`, {}
    );
  }

  copyRiskAssessment(assessmentId: number): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(
      `${this.apiUrl}/CopyRiskAssessment/${assessmentId}`, {}
    );
  }

  getDefaultRiskCategories(): Observable<string[]> {
    return this.http.get<ApiResponse<string[]>>(
      `${this.apiUrl}/GetDefaultRiskCategories`
    ).pipe(map(r => r.result || []));
  }

  // ── Risk Items ────────────────────────────────────────────

  createRiskItem(assessmentId: number, request: CreateRiskItemRequest): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(
      `${this.apiUrl}/CreateRiskItem/${assessmentId}`, request
    );
  }

  updateRiskItem(assessmentId: number, request: UpdateRiskItemRequest): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(
      `${this.apiUrl}/UpdateRiskItem/${assessmentId}`, request
    );
  }

  deleteRiskItem(assessmentId: number, itemId: number): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(
      `${this.apiUrl}/DeleteRiskItem/${assessmentId}/${itemId}`
    );
  }

  reorderRiskItems(assessmentId: number, request: ReorderItemsRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(
      `${this.apiUrl}/ReorderRiskItems/${assessmentId}`, request
    );
  }

  // ── Goals ─────────────────────────────────────────────────

  getGoals(status?: string, year?: number, category?: string): Observable<GoalListDto[]> {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    if (year) params = params.set('year', year.toString());
    if (category) params = params.set('category', category);
    return this.http.get<ApiResponse<GoalListDto[]>>(
      `${this.apiUrl}/GetGoals`, { params }
    ).pipe(map(r => r.result || []));
  }

  getGoal(goalId: number): Observable<GoalDetailDto | null> {
    return this.http.get<ApiResponse<GoalDetailDto | null>>(
      `${this.apiUrl}/GetGoal/${goalId}`
    ).pipe(map(r => r.result));
  }

  createGoal(request: CreateGoalRequest): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(
      `${this.apiUrl}/CreateGoal`, request
    );
  }

  updateGoal(request: UpdateGoalRequest): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(
      `${this.apiUrl}/UpdateGoal`, request
    );
  }

  deleteGoal(goalId: number): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(
      `${this.apiUrl}/DeleteGoal/${goalId}`
    );
  }

  updateGoalStatus(goalId: number, request: UpdateGoalStatusRequest): Observable<ApiResponse<boolean>> {
    return this.http.patch<ApiResponse<boolean>>(
      `${this.apiUrl}/UpdateGoalStatus/${goalId}`, request
    );
  }

  getGoalCategories(): Observable<string[]> {
    return this.http.get<ApiResponse<string[]>>(
      `${this.apiUrl}/GetGoalCategories`
    ).pipe(map(r => r.result || []));
  }

  getGoalSummary(year?: number): Observable<GoalSummaryDto> {
    let params = new HttpParams();
    if (year) params = params.set('year', year.toString());
    return this.http.get<ApiResponse<GoalSummaryDto>>(
      `${this.apiUrl}/GetGoalSummary`, { params }
    ).pipe(map(r => r.result));
  }

  // ── Goal Actions ──────────────────────────────────────────

  createGoalAction(goalId: number, request: CreateActionRequest): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(
      `${this.apiUrl}/CreateGoalAction/${goalId}`, request
    );
  }

  updateGoalAction(goalId: number, request: UpdateActionRequest): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(
      `${this.apiUrl}/UpdateGoalAction/${goalId}`, request
    );
  }

  updateGoalActionStatus(goalId: number, actionId: number, request: UpdateActionStatusRequest): Observable<ApiResponse<boolean>> {
    return this.http.patch<ApiResponse<boolean>>(
      `${this.apiUrl}/UpdateGoalActionStatus/${goalId}/${actionId}`, request
    );
  }

  deleteGoalAction(goalId: number, actionId: number): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(
      `${this.apiUrl}/DeleteGoalAction/${goalId}/${actionId}`
    );
  }

  // ══════════════════════════════════════════════════════════════
  //  Internal Control (Internkontrollhjulet §5)
  // ══════════════════════════════════════════════════════════════

  getInternalControlOverview(): Observable<InternalControlOverviewDto> {
    return this.http.get<ApiResponse<InternalControlOverviewDto>>(
      `${this.apiUrl}/GetInternalControlOverview`
    ).pipe(map(r => r.result));
  }

  getRequirementDetail(requirementNo: number): Observable<InternalControlRequirementDto> {
    return this.http.get<ApiResponse<InternalControlRequirementDto>>(
      `${this.apiUrl}/GetRequirementDetail/${requirementNo}`
    ).pipe(map(r => r.result));
  }

  updateRequirementStatus(request: UpdateRequirementStatusRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(
      `${this.apiUrl}/UpdateRequirementStatus`, request
    );
  }

  setReviewDate(request: SetReviewDateRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(
      `${this.apiUrl}/SetReviewDate`, request
    );
  }

  addControlDocument(request: AddControlDocumentRequest): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(
      `${this.apiUrl}/AddControlDocument`, request
    );
  }

  removeControlDocument(docLinkId: number): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(
      `${this.apiUrl}/RemoveControlDocument/${docLinkId}`
    );
  }

  addControlAction(request: AddControlActionRequest): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(
      `${this.apiUrl}/AddControlAction`, request
    );
  }

  updateControlAction(request: UpdateControlActionRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(
      `${this.apiUrl}/UpdateControlAction`, request
    );
  }

  deleteControlAction(actionId: number): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(
      `${this.apiUrl}/DeleteControlAction/${actionId}`
    );
  }

  saveInternalControlReview(request: SaveReviewRequest): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(
      `${this.apiUrl}/SaveInternalControlReview`, request
    );
  }

  getReviewHistory(requirementNo?: number): Observable<InternalControlReviewDto[]> {
    let params = new HttpParams();
    if (requirementNo) params = params.set('requirementNo', requirementNo.toString());
    return this.http.get<ApiResponse<InternalControlReviewDto[]>>(
      `${this.apiUrl}/GetReviewHistory`, { params }
    ).pipe(map(r => r.result));
  }

  getAggregatedModuleStatus(): Observable<{ [key: number]: ModuleStatusDto }> {
    return this.http.get<ApiResponse<{ [key: number]: ModuleStatusDto }>>(
      `${this.apiUrl}/GetAggregatedModuleStatus`
    ).pipe(map(r => r.result));
  }

  getMyActions(): Observable<ApiResponse<UnifiedActionDto[]>> {
    return this.http.get<ApiResponse<UnifiedActionDto[]>>(
      `${this.apiUrl}/GetMyActions`
    );
  }

  getAllActions(): Observable<ApiResponse<UnifiedActionDto[]>> {
    return this.http.get<ApiResponse<UnifiedActionDto[]>>(
      `${this.apiUrl}/GetAllActions`
    );
  }

  // ══════════════════════════════════════════════════════════════
  //  Chemical Registry (Stoffkartotek)
  // ══════════════════════════════════════════════════════════════

  private get chemicalApiUrl(): string {
    return `${this.configService.get('apiBaseUrl')}/HmsChemical`;
  }

  getChemicals(search?: string, category?: string, status?: string, sdsStatus?: string,
    sortField: string = 'ChemicalName', sortDir: string = 'asc'): Observable<ApiResponse<ChemicalListDto[]>> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (category) params = params.set('category', category);
    if (status) params = params.set('status', status);
    if (sdsStatus) params = params.set('sdsStatus', sdsStatus);
    params = params.set('sortField', sortField).set('sortDir', sortDir);
    return this.http.get<ApiResponse<ChemicalListDto[]>>(`${this.chemicalApiUrl}/GetChemicals`, { params });
  }

  getChemicalDetail(id: number): Observable<ApiResponse<ChemicalDetailDto>> {
    return this.http.get<ApiResponse<ChemicalDetailDto>>(`${this.chemicalApiUrl}/GetChemicalDetail?id=${id}`);
  }

  saveChemical(request: SaveChemicalRequest): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.chemicalApiUrl}/SaveChemical`, request);
  }

  deleteChemical(chemicalId: number): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.chemicalApiUrl}/DeleteChemical`, { chemicalID: chemicalId });
  }

  setExposures(chemicalId: number, exposures: ExposureItem[]): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.chemicalApiUrl}/SetExposures`, { chemicalID: chemicalId, exposures });
  }

  getChemicalTrainings(chemicalId: number): Observable<ApiResponse<ChemicalTrainingDto[]>> {
    return this.http.get<ApiResponse<ChemicalTrainingDto[]>>(`${this.chemicalApiUrl}/GetChemicalTrainings?chemicalId=${chemicalId}`);
  }

  setChemicalTrainings(chemicalId: number, trainings: TrainingItem[]): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.chemicalApiUrl}/SetChemicalTrainings`, { chemicalID: chemicalId, trainings });
  }

  // ── Share Tokens (QR / Share Link) ─────────────────────────────

  createShareToken(chemicalId: number, expiresInDays?: number): Observable<ApiResponse<ShareTokenDto>> {
    return this.http.post<ApiResponse<ShareTokenDto>>(`${this.chemicalApiUrl}/CreateShareToken`, { chemicalID: chemicalId, expiresInDays });
  }

  getShareTokens(chemicalId: number): Observable<ApiResponse<ShareTokenDto[]>> {
    return this.http.get<ApiResponse<ShareTokenDto[]>>(`${this.chemicalApiUrl}/GetShareTokens?chemicalId=${chemicalId}`);
  }

  revokeShareToken(shareTokenId: number): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.chemicalApiUrl}/RevokeShareToken`, { shareTokenID: shareTokenId });
  }

  getChemicalByShareToken(token: string): Observable<ApiResponse<ChemicalPublicDto>> {
    const domain = this.configService.get('domain') || '';
    return this.http.get<ApiResponse<ChemicalPublicDto>>(`${this.chemicalApiUrl}/GetChemicalByShareToken?token=${encodeURIComponent(token)}&domain=${encodeURIComponent(domain)}`);
  }

  uploadSds(chemicalId: number, file: File): Observable<ApiResponse<boolean>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ApiResponse<boolean>>(`${this.chemicalApiUrl}/UploadSds?chemicalId=${chemicalId}`, formData);
  }

  getSdsDownloadUrl(chemicalId: number): Observable<ApiResponse<string>> {
    return this.http.get<ApiResponse<string>>(`${this.chemicalApiUrl}/GetSdsDownloadUrl?chemicalId=${chemicalId}`);
  }

  deleteSds(chemicalId: number): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.chemicalApiUrl}/DeleteSds`, { chemicalID: chemicalId });
  }

  getChemicalDashboard(): Observable<ApiResponse<ChemicalDashboardDto>> {
    return this.http.get<ApiResponse<ChemicalDashboardDto>>(`${this.chemicalApiUrl}/GetChemicalDashboard`);
  }

  getExpiringSds(withinDays: number = 30): Observable<ApiResponse<ChemicalListDto[]>> {
    return this.http.get<ApiResponse<ChemicalListDto[]>>(`${this.chemicalApiUrl}/GetExpiringSds?withinDays=${withinDays}`);
  }

  getChemicalAuditHistory(chemicalId: number): Observable<ApiResponse<ChemicalAuditDto[]>> {
    return this.http.get<ApiResponse<ChemicalAuditDto[]>>(`${this.chemicalApiUrl}/GetChemicalAuditHistory?chemicalId=${chemicalId}`);
  }

  lookupCasNumber(casNumber: string): Observable<ApiResponse<PubChemLookupResult>> {
    return this.http.get<ApiResponse<PubChemLookupResult>>(`${this.chemicalApiUrl}/LookupCasNumber?casNumber=${encodeURIComponent(casNumber)}`);
  }

  parseLabelImage(file: File): Observable<ApiResponse<LabelScanResult>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ApiResponse<LabelScanResult>>(`${this.chemicalApiUrl}/ParseLabelImage`, formData);
  }

  parseSdsDocument(file: File): Observable<ApiResponse<LabelScanResult>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ApiResponse<LabelScanResult>>(`${this.chemicalApiUrl}/ParseSdsDocument`, formData);
  }

  analyzeWithKai(chemicalId: number, analysisType: string): Observable<ApiResponse<KaiChemicalAnalysisDto>> {
    return this.http.post<ApiResponse<KaiChemicalAnalysisDto>>(`${this.chemicalApiUrl}/AnalyzeWithKai`, { chemicalID: chemicalId, analysisType });
  }

  // ══════════════════════════════════════════════════════════════
  //  Punkt 1: Risk item → deviation link
  // ══════════════════════════════════════════════════════════════

  linkRiskItemToDeviation(itemId: number, deviationId: number): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(
      `${this.apiUrl}/LinkRiskItemToDeviation/${itemId}/${deviationId}`, {}
    );
  }

  // ══════════════════════════════════════════════════════════════
  //  Punkt 2: Risk Actions CRUD
  // ══════════════════════════════════════════════════════════════

  createRiskAction(itemId: number, request: CreateRiskActionRequest): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(
      `${this.apiUrl}/CreateRiskAction/${itemId}`, request
    );
  }

  updateRiskAction(itemId: number, request: UpdateRiskActionRequest): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(
      `${this.apiUrl}/UpdateRiskAction/${itemId}`, request
    );
  }

  updateRiskActionStatus(itemId: number, actionId: number, request: UpdateActionStatusRequest): Observable<ApiResponse<boolean>> {
    return this.http.patch<ApiResponse<boolean>>(
      `${this.apiUrl}/UpdateRiskActionStatus/${itemId}/${actionId}`, request
    );
  }

  deleteRiskAction(itemId: number, actionId: number): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(
      `${this.apiUrl}/DeleteRiskAction/${itemId}/${actionId}`
    );
  }

  // ══════════════════════════════════════════════════════════════
  //  Punkt 4: Konfigurerbare mål-kategorier
  // ══════════════════════════════════════════════════════════════

  updateGoalCategories(categories: string[]): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(
      `${this.apiUrl}/UpdateGoalCategories`, { categories }
    );
  }

  // ══════════════════════════════════════════════════════════════
  //  Punkt 5: Historikk-sammenligning
  // ══════════════════════════════════════════════════════════════

  getRiskAssessmentHistory(assessmentId: number): Observable<ApiResponse<RiskAssessmentHistoryItemDto[]>> {
    return this.http.get<ApiResponse<RiskAssessmentHistoryItemDto[]>>(
      `${this.apiUrl}/GetRiskAssessmentHistory/${assessmentId}`
    );
  }

  compareRiskAssessments(id1: number, id2: number): Observable<ApiResponse<RiskAssessmentComparisonDto>> {
    return this.http.get<ApiResponse<RiskAssessmentComparisonDto>>(
      `${this.apiUrl}/CompareRiskAssessments?id1=${id1}&id2=${id2}`
    );
  }
}

// ══════════════════════════════════════════════════════════════
//  Internal Control Interfaces
// ══════════════════════════════════════════════════════════════

export interface InternalControlOverviewDto {
  requirements: InternalControlRequirementDto[];
  overallCompliancePercent: number;
  lastFullReviewDate?: string;
  nextFullReviewDate?: string;
}

export interface InternalControlRequirementDto {
  controlID: number;
  requirementNo: number;
  requirementTitle: string;
  status: string;
  compliancePercent: number;
  notes?: string;
  legalText?: string;
  nextReviewDate?: string;
  reviewIntervalDays?: number;
  lastReviewDate?: string;
  lastReviewByName?: string;
  openActionCount: number;
  linkedModuleStatus?: ModuleStatusDto;
  documents?: InternalControlDocumentDto[];
  actions?: InternalControlActionDto[];
  recentReviews?: InternalControlReviewDto[];
}

export interface ModuleStatusDto {
  activeGoalCount?: number;
  goalCompletionAvg?: number;
  activeRiskAssessments?: number;
  highRiskItems?: number;
  openDeviations?: number;
  criticalDeviations?: number;
  safetyRoundsThisYear?: number;
  lastSafetyRound?: string;
}

export interface UnifiedActionDto {
  actionId: number;
  sourceModule: string;
  sourceId: number;
  parentSourceId: number;
  sourceTitle?: string;
  actionTitle: string;
  actionDescription?: string;
  actionStatus: string;
  assignedToUserId?: number;
  assignedToName?: string;
  dueDate?: string;
  completedDate?: string;
  completionNotes?: string;
  createdByName?: string;
  createdDate: string;
}

export interface InternalControlDocumentDto {
  docLinkID: number;
  requirementNo: number;
  documentType: string;
  referenceID?: number;
  externalUrl?: string;
  title: string;
  description?: string;
  createdByName?: string;
  createdDate?: string;
}

export interface InternalControlActionDto {
  actionID: number;
  requirementNo: number;
  title: string;
  description?: string;
  status: string;
  assignedTo?: number;
  assignedToName?: string;
  dueDate?: string;
  completedDate?: string;
  completionNotes?: string;
  createdDate?: string;
}

export interface InternalControlReviewDto {
  reviewID: number;
  requirementNo?: number;
  reviewDate: string;
  reviewedByName?: string;
  previousStatus?: string;
  newStatus?: string;
  compliancePercent: number;
  comments?: string;
  kaiAnalysis?: string;
}

export interface UpdateRequirementStatusRequest {
  requirementNo: number;
  status: string;
  compliancePercent: number;
  notes?: string;
}

export interface SetReviewDateRequest {
  requirementNo: number;
  nextReviewDate: string;
  reviewIntervalDays?: number;
}

export interface AddControlDocumentRequest {
  requirementNo: number;
  documentType: string;
  referenceID?: number;
  externalUrl?: string;
  title: string;
  description?: string;
}

export interface AddControlActionRequest {
  requirementNo: number;
  title: string;
  description?: string;
  assignedTo?: number;
  dueDate?: string;
}

export interface UpdateControlActionRequest {
  actionID: number;
  title?: string;
  description?: string;
  status?: string;
  assignedTo?: number;
  dueDate?: string;
  completionNotes?: string;
}

export interface SaveReviewRequest {
  requirementNo?: number;
  newStatus: string;
  compliancePercent: number;
  comments?: string;
  kaiAnalysis?: string;
}

// ══════════════════════════════════════════════════════════════
//  Chemical Registry Interfaces (Stoffkartotek)
// ══════════════════════════════════════════════════════════════

export interface ChemicalListDto {
  chemicalID: number;
  chemicalName: string;
  tradeName?: string;
  manufacturer?: string;
  casNumber?: string;
  category?: string;
  status: string;
  hazardClasses?: string;
  signalWord?: string;
  location?: string;
  sdsType?: string;
  sdsExpiryDate?: string;
  updatedDate?: string;
  createdDate?: string;
}

export interface ChemicalDetailDto {
  chemicalID: number;
  chemicalName: string;
  tradeName?: string;
  manufacturer?: string;
  casNumber?: string;
  category?: string;
  usageArea?: string;
  status: string;
  hazardClasses?: string;
  hStatements?: string;
  pStatements?: string;
  signalWord?: string;
  sdsType?: string;
  sdsBlobFileName?: string;
  sdsOriginalFileName?: string;
  sdsExternalUrl?: string;
  sdsRevisionDate?: string;
  sdsExpiryDate?: string;
  location?: string;
  quantity?: number;
  quantityUnit?: string;
  maxQuantity?: number;
  requiredPPE?: string;
  firstAidMeasures?: string;
  fireExtinguisher?: string;
  spillHandling?: string;
  oelTwaValue?: number;
  oelTwaUnit?: string;
  oelStelValue?: number;
  oelStelUnit?: string;
  oelCeilingValue?: number;
  oelCeilingUnit?: string;
  oelNotes?: string;
  substitutionReviewed: boolean;
  canBeSubstituted?: string;
  substituteChemical?: string;
  substitutionReason?: string;
  substitutionDate?: string;
  substitutionReviewedBy?: number;
  pubChemCID?: number;
  createdBy?: number;
  createdDate?: string;
  updatedBy?: number;
  updatedDate?: string;
  exposures: ChemicalExposureDto[];
  trainings: ChemicalTrainingDto[];
}

export interface ChemicalExposureDto {
  exposureID: number;
  chemicalID: number;
  exposureType?: string;
  exposureValue?: string;
  exposureFrequency?: string;
  notes?: string;
  createdDate?: string;
}

export interface ChemicalDashboardDto {
  totalCount: number;
  activeCount: number;
  expiredSdsCount: number;
  expiringSoonSdsCount: number;
  missingSdsCount: number;
  noSubstitutionReviewCount: number;
  hazardDistribution: HazardDistributionItem[];
  categoryDistribution: CategoryDistributionItem[];
}

export interface HazardDistributionItem {
  hazardClass: string;
  count: number;
}

export interface CategoryDistributionItem {
  category: string;
  count: number;
}

export interface ChemicalAuditDto {
  auditID: number;
  chemicalID: number;
  fieldName: string;
  oldValue?: string;
  newValue?: string;
  changedBy: number;
  changedByName?: string;
  changedDate: string;
}

export interface SaveChemicalRequest {
  chemicalID?: number;
  chemicalName: string;
  tradeName?: string;
  manufacturer?: string;
  casNumber?: string;
  category?: string;
  usageArea?: string;
  status: string;
  hazardClasses?: string;
  hStatements?: string;
  pStatements?: string;
  signalWord?: string;
  sdsType?: string;
  sdsExternalUrl?: string;
  sdsRevisionDate?: string;
  sdsExpiryDate?: string;
  location?: string;
  quantity?: number;
  quantityUnit?: string;
  maxQuantity?: number;
  requiredPPE?: string;
  firstAidMeasures?: string;
  fireExtinguisher?: string;
  spillHandling?: string;
  oelTwaValue?: number;
  oelTwaUnit?: string;
  oelStelValue?: number;
  oelStelUnit?: string;
  oelCeilingValue?: number;
  oelCeilingUnit?: string;
  oelNotes?: string;
  substitutionReviewed: boolean;
  canBeSubstituted?: string;
  substituteChemical?: string;
  substitutionReason?: string;
  substitutionDate?: string;
}

export interface ExposureItem {
  exposureType: string;
  exposureValue?: string;
  exposureFrequency?: string;
  notes?: string;
}

export interface ChemicalTrainingDto {
  trainingID: number;
  chemicalID: number;
  employeeName: string;
  employeeNo?: string;
  trainingDate: string;
  trainingType: string;
  trainedBy?: string;
  notes?: string;
  validUntil?: string;
  createdBy: number;
  createdDate: string;
}

export interface TrainingItem {
  employeeName: string;
  employeeNo?: string;
  trainingDate: string;
  trainingType: string;
  trainedBy?: string;
  notes?: string;
  validUntil?: string;
}

export interface ShareTokenDto {
  shareTokenID: number;
  chemicalID: number;
  shareToken: string;
  expiresAt?: string;
  isRevoked: boolean;
  createdBy: number;
  createdDate: string;
}

export interface CreateShareTokenRequest {
  chemicalID: number;
  expiresInDays?: number;
}

export interface ChemicalPublicDto {
  chemicalName: string;
  tradeName?: string;
  manufacturer?: string;
  casNumber?: string;
  category?: string;
  usageArea?: string;
  hazardClasses?: string;
  hStatements?: string;
  pStatements?: string;
  signalWord?: string;
  requiredPPE?: string;
  firstAidMeasures?: string;
  fireExtinguisher?: string;
  spillHandling?: string;
  location?: string;
  hasSds: boolean;
  sdsDownloadUrl?: string;
  sdsExternalUrl?: string;
  sdsRevisionDate?: string;
  updatedDate?: string;
}

export interface PubChemLookupResult {
  found: boolean;
  cid?: number;
  hazardClasses?: string[];
  hStatements?: string[];
  pStatements?: string[];
  signalWord?: string;
  pictograms?: string[];
  requiredPPE?: string[];
}

export interface LabelScanResult {
  success: boolean;
  chemicalName?: string;
  tradeName?: string;
  manufacturer?: string;
  casNumber?: string;
  signalWord?: string;
  hazardClasses: string[];
  hStatements: string[];
  pStatements: string[];
  pictograms: string[];
  requiredPPE: string[];
  usageArea?: string;
  firstAidMeasures?: string;
  errorMessage?: string;
  confidence: number;
  dataSource: string;
}

export interface KaiChemicalAnalysisDto {
  chemicalID: number;
  analysisType: string;
  result: string;
}

// ══════════════════════════════════════════════════════════════
//  Risk Action Interfaces (Punkt 2)
// ══════════════════════════════════════════════════════════════

export interface HmsRiskActionDto {
  actionId: number;
  riskItemId: number;
  actionTitle: string;
  actionDescription?: string;
  assignedToUserId?: number;
  assignedToName?: string;
  dueDate?: string;
  actionStatus: string;
  completedDate?: string;
  completedByUserId?: number;
  completionNotes?: string;
  createdByUserId: number;
  createdDate: string;
  modifiedDate?: string;
}

export interface CreateRiskActionRequest {
  actionTitle: string;
  actionDescription?: string;
  assignedToUserId?: number;
  dueDate?: string;
}

export interface UpdateRiskActionRequest {
  actionId: number;
  actionTitle: string;
  actionDescription?: string;
  assignedToUserId?: number;
  dueDate?: string;
}

// ══════════════════════════════════════════════════════════════
//  Risk Assessment History & Comparison (Punkt 5)
// ══════════════════════════════════════════════════════════════

export interface RiskAssessmentHistoryItemDto {
  assessmentId: number;
  title: string;
  status: string;
  createdDate: string;
  completedDate?: string;
  periodFrom?: string;
  periodTo?: string;
  itemCount: number;
  highRiskCount: number;
  criticalRiskCount: number;
}

export interface RiskAssessmentComparisonDto {
  assessment1Id: number;
  assessment1Title: string;
  assessment2Id: number;
  assessment2Title: string;
  rows: RiskItemComparisonRow[];
}

export interface RiskItemComparisonRow {
  category: string;
  description?: string;
  changeType: 'Added' | 'Removed' | 'Changed' | 'Unchanged';
  previousProbability?: number;
  previousConsequence?: number;
  previousRiskScore?: number;
  currentProbability?: number;
  currentConsequence?: number;
  currentRiskScore?: number;
}

// ══════════════════════════════════════════════════════════════
//  Goal Categories (Punkt 4)
// ══════════════════════════════════════════════════════════════

export interface UpdateGoalCategoriesRequest {
  categories: string[];
}
